import { FormEvent, useState } from 'react';
import { submitSignup } from '@/api/newsletter';

// Shared across every capture point (Latest News inline capture, slide-in
// card): one signup anywhere means no capture point asks again on later
// visits.
export const JOINED_STORAGE_KEY = 'joinedNewsletter';

/** 'invalid' = the email failed validation; 'failed' = the Firestore write
    threw (offline, rules, quota). Both render visibly - never a silent
    failure dressed as success (CLAUDE.md §10). */
export type SignupError = 'invalid' | 'failed' | null;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const hasJoined = () => {
  try {
    return localStorage.getItem(JOINED_STORAGE_KEY) === 'true';
  } catch {
    // Private browsing / storage disabled: fall back to asking again.
    return false;
  }
};

/**
 * Email/validation/persistence state shared by all newsletter capture points.
 * `done` starts true when a previous visit already joined; a capture point
 * that should be fully hidden in that case checks hasJoined() itself.
 */
export const useNewsletterSignup = (source: string, project?: string) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<SignupError>(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(hasJoined);

  const changeEmail = (value: string) => {
    setEmail(value);
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!isValidEmail(email)) {
      setError('invalid');
      return;
    }

    setSending(true);
    try {
      await submitSignup({ email: email.trim(), source, project });
    } catch (err) {
      // Logged with context so a rules rejection is diagnosable from the
      // console alone (CLAUDE.md §8).
      console.error(`[newsletter] signup write failed (source=${source}):`, err);
      setError('failed');
      setSending(false);
      return;
    }

    // Success side effects only AFTER the write resolved: the success copy
    // and the suppression flag must never claim a signup that wasn't stored.
    try {
      localStorage.setItem(JOINED_STORAGE_KEY, 'true');
    } catch {
      // Storage unavailable - success state still shows for this visit.
    }
    window.dataLayer?.push({ event: 'newsletter_signup', source, project });
    setSending(false);
    setDone(true);
  };

  return { email, changeEmail, error, sending, done, submit };
};
